import { z } from 'zod';
import { IssuedTokensSchema } from './mobile-auth';
import { DevicePlatformSchema } from './mobile-push';
/**
 * Admin mobile sign-in contract — apps/api `modules/admin-mobile-auth`
 * (admin-mobile-auth.controller.ts, admin-device-sessions.controller.ts).
 *
 * The admin app is a SEPARATE door from the client app: its tokens carry
 * `aud: 'admin-mobile'` and a device-session id (`sid`), are refused on every
 * client route (403 `ADMIN_MOBILE_TOKEN_NOT_ALLOWED`), and live in their own
 * collection `admin_device_sessions` — one active session per device.
 *
 * Access 15m, refresh 24h, rotated on every refresh; presenting an
 * already-rotated refresh token revokes the device session (`REFRESH_REUSED`).
 *
 * The login answer is an ENVELOPE keyed by `step` so a second factor can be
 * added later (`step: 'second_factor'`) without breaking an older app: an app
 * that does not know a step shows a generic "update the app" screen.
 */
/** The device the admin app runs on. `deviceId` is an install-scoped UUID the app generates once. */
export const AdminMobileDeviceSchema = z.object({
    deviceId: z.string().uuid(),
    platform: DevicePlatformSchema,
    model: z.string().trim().min(1).max(100),
    appVersion: z.string().trim().min(1).max(32),
});
/** `POST /api/auth/admin/mobile-login`. Any non-empty password — the account's rules apply at set time. */
export const AdminMobileLoginRequestSchema = z.object({
    email: z.string().trim().email().max(254),
    password: z.string().min(1).max(128),
    device: AdminMobileDeviceSchema,
});
/** The device session a token pair belongs to. */
export const AdminMobileSessionRefSchema = z.object({
    id: z.string(),
    /** When the current refresh token expires (ISO); a refresh moves it forward. */
    expiresAt: z.string(),
});
export const AdminMobileAuthenticatedSchema = z.object({
    step: z.literal('authenticated'),
    tokens: IssuedTokensSchema,
    session: AdminMobileSessionRefSchema,
});
/**
 * 200 body of mobile-login. Only `authenticated` exists today; the union is
 * where `second_factor` (TOTP) will join.
 */
export const AdminMobileLoginResponseSchema = z.discriminatedUnion('step', [
    AdminMobileAuthenticatedSchema,
]);
/** `POST /api/auth/admin/mobile-refresh` and `POST /api/auth/admin/mobile-logout` body. */
export const AdminMobileRefreshRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
/** 200 body of mobile-refresh: the rotated pair and the same session. */
export const AdminMobileRefreshResponseSchema = AdminMobileAuthenticatedSchema;
/** Why a device session stopped (`admin_device_sessions.revokeReason`). */
export const AdminDeviceSessionRevokeReasonSchema = z.enum([
    'logout',
    'reuse',
    'superseded',
    'role_removed',
    'account_deleted',
    'password_changed',
    'superadmin',
]);
/** One device session as the superadmin list shows it. Never a token or hash. */
export const AdminDeviceSessionSchema = z.object({
    id: z.string(),
    userId: z.string(),
    deviceId: z.string(),
    platform: DevicePlatformSchema,
    model: z.string(),
    appVersion: z.string(),
    active: z.boolean(),
    generation: z.number().int().min(0),
    createdAt: z.string(),
    lastSeenAt: z.string(),
    lastIp: z.string().nullable(),
    revokedAt: z.string().nullable(),
    revokedBy: z.string().nullable(),
    revokeReason: AdminDeviceSessionRevokeReasonSchema.nullable(),
});
/** `POST /api/admin/users/:userId/mobile-sessions/revoke-all` 200 body. */
export const AdminDeviceSessionsRevokeAllResultSchema = z.object({
    revoked: z.number().int().min(0),
});
export const AdminMobileAuthErrorCodeSchema = z.enum([
    'VALIDATION_FAILED',
    /** Every refusal of mobile-login: unknown email, wrong password, not an admin. */
    'INVALID_CREDENTIALS',
    'TOO_MANY_ATTEMPTS',
    /** `ADMIN_MOBILE_AUTH_ENABLED` is off (503). */
    'ADMIN_MOBILE_DISABLED',
    /** Refresh token invalid, expired, or its session is no longer active (401). */
    'INVALID_REFRESH_TOKEN',
    /** An already-rotated refresh token was presented; the session was revoked (401). */
    'REFRESH_REUSED',
    /** An admin-mobile access token was presented on a client route (403). */
    'ADMIN_MOBILE_TOKEN_NOT_ALLOWED',
    'SESSION_NOT_FOUND',
    /** An admin-mobile access token that fails verification — expired, forged, wrong type (401). */
    'INVALID_ACCESS_TOKEN',
    /** An admin-mobile access token on an admin route outside the phone allowlist (403). */
    'ADMIN_MOBILE_ROUTE_NOT_ALLOWED',
    /** The token's device session is revoked, superseded or not the token's own (401). */
    'ADMIN_SESSION_REVOKED',
    /** The token's account no longer holds the `admin` role (403). */
    'ADMIN_ROLE_REQUIRED',
    /** A client (non-admin-mobile) access token presented on an admin-only route (403). */
    'ADMIN_ROUTE_TOKEN_NOT_ALLOWED',
]);
//# sourceMappingURL=admin-mobile-auth.js.map